import { useState, useMemo } from "react";
import { useListTasks, useListInstances, Task, RecurrencePattern } from "@/lib/api/checklist";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getDateRangeForRecurrence } from "@/lib/utils/recurrence";
import { YearlyBreakdownView } from "./YearlyBreakdownView";
import { Skeleton } from "@/components/ui/skeleton";
import moment from "moment-timezone";

interface YearlyBreakdownCardProps {
  searchQuery?: string;
}

export function YearlyBreakdownCard({ searchQuery = '' }: YearlyBreakdownCardProps) {
  const [lookbackYears, setLookbackYears] = useState(3);
  const [isFiscalYear, setIsFiscalYear] = useState(false);

  // Fetch all tasks
  const tasksResult = useListTasks({
    is_active: true,
    limit: 100000,
    search: searchQuery || undefined,
  });

  // Calculate date range for yearly
  const dateRange = useMemo(() => {
    if (isFiscalYear) {
      // For fiscal year, we need to calculate from July to June
      const now = moment();
      const currentFiscalYear = now.month() >= 6 ? now.year() + 1 : now.year();
      
      // Start from July of (currentFiscalYear - lookbackYears - 1)
      const startYear = currentFiscalYear - lookbackYears;
      const start = moment().year(startYear).month(6).startOf('month'); // July

      // End at June of current fiscal year
      const end = moment().year(currentFiscalYear).month(5).endOf('month'); // June
      console.warn("end", now.toDate(), currentFiscalYear, startYear, start.toDate(), end.toDate())
      
      return { start: start.toDate(), end: end.toDate() };
    } else {
      return getDateRangeForRecurrence(RecurrencePattern.ANNUALLY, lookbackYears);
    }
  }, [lookbackYears, isFiscalYear]);

  // Fetch all instances within the date range
  const instancesResult = useListInstances({
    due_date_from: dateRange.start.toISOString(),
    due_date_to: dateRange.end.toISOString(),
    limit: 100000,
    search: searchQuery || undefined,
  });

  const tasks = tasksResult?.data?.data?.tasks || [];
  const instances = instancesResult?.data?.data?.instances || [];
  const isLoading = tasksResult.isLoading || instancesResult.isLoading;

  // Filter tasks by yearly recurrence pattern
  const filteredTasks = useMemo(() => {
    return tasks.filter((task: Task) => task.recurrence_pattern === RecurrencePattern.ANNUALLY);
  }, [tasks]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Yearly Tasks</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Label htmlFor="lookback-years" className="whitespace-nowrap">
              Show last:
            </Label>
            <Input
              id="lookback-years"
              type="number"
              min="1"
              max="10"
              value={lookbackYears}
              onChange={(e) => setLookbackYears(parseInt(e.target.value) || 1)}
              className="w-20"
            />
            <span className="text-sm text-muted-foreground">years</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Switch
              id="fiscal-year-yearly"
              checked={isFiscalYear}
              onCheckedChange={setIsFiscalYear}
            />
            <Label htmlFor="fiscal-year-yearly" className="text-sm">
              Fiscal Year View
            </Label>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No tasks found with yearly recurrence
          </div>
        ) : (
          <YearlyBreakdownView
            tasks={filteredTasks}
            instances={instances}
            lookbackYears={isFiscalYear ? lookbackYears : lookbackYears - 1}
            isFiscalYear={isFiscalYear}
          />
        )}
      </CardContent>
    </Card>
  );
}
